import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCrown, FaGift } from 'react-icons/fa';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import TrialRequestModal from './TrialRequestModal';
import { usePremiumStatus } from '../../hooks/usePremiumStatus';
import { useUserSession } from '../../hooks/useUserSession';
import { usePublicConfig } from '../../hooks/usePublicConfig';
import { getDashboardPremiumOfferPath } from '../../config/constants';

/**
 * @typedef {Object} PremiumGateProps
 * @property {React.ReactNode} children
 * @property {string} [featureName]
 * @property {string} [featureId] - ID backend (ex. PREMIUM_FEATURE_IDS) : si la fonctionnalité est désactivée côté admin (gratuite pour tous), les children sont affichés sans blocage.
 * @property {boolean} [showModalOnlyOnClick]
 */

/**
 * Enveloppe une zone réservée aux utilisateurs premium.
 * Si l'utilisateur n'a pas accès : affiche le modal d'upgrade (et éventuellement un message à la place du contenu).
 * Si hasAccess : affiche les children.
 * @param {PremiumGateProps} props
 */
function PremiumGate({
  children,
  featureName = 'Cette fonctionnalité',
  featureId = null,
  showModalOnlyOnClick = false,
}) {
  const navigate = useNavigate();
  const { hasPremiumAccess, eligibleForTrial, requestTrial, refreshPremiumStatus } = usePremiumStatus();
  const { user } = useUserSession();
  const { config } = usePublicConfig();

  const isGatedByConfig =
    !featureId ||
    config.premiumFeatureGating == null ||
    config.premiumFeatureGating[featureId] !== false;
  const [showModal, setShowModal] = useState(false);
  const [showTrialRequest, setShowTrialRequest] = useState(false);
  const paymentsEnabled = config.paymentsEnabled;

  const handleRequestTrial = async () => {
    return requestTrial();
  };

  const handleTrialRequestClose = () => {
    setShowTrialRequest(false);
    refreshPremiumStatus();
  };

  if (hasPremiumAccess || !isGatedByConfig) {
    return children;
  }

  const goToPremiumOffer = () => navigate(getDashboardPremiumOfferPath());
  const openOfferModal = () => setShowModal(true);
  const openTrialModal = () => setShowTrialRequest(true);
  const openTrialFromModal = () => {
    setShowModal(false);
    setShowTrialRequest(true);
  };

  if (showModalOnlyOnClick) {
    return (
      <>
        <button type="button" className="premium-gate-trigger" onClick={openOfferModal}>
          <FaCrown className="premium-gate-trigger__icon" aria-hidden />
          <span>Débloquer (Premium)</span>
        </button>
        {showModal && (
          <PremiumUpgradeModal
            featureName={featureName}
            eligibleForTrial={eligibleForTrial}
            onActivateTrial={openTrialFromModal}
            onClose={() => setShowModal(false)}
          />
        )}
        {showTrialRequest && (
          <TrialRequestModal
            userEmail={user?.email || null}
            onClose={handleTrialRequestClose}
            onConfirm={handleRequestTrial}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className="premium-gate-blocked"
        role="region"
        aria-labelledby="premium-gate-title"
      >
        <div className="premium-gate-blocked__inner">
          <div className="premium-gate-blocked__iconWrap" aria-hidden>
            <FaCrown className="premium-gate-blocked__crown" />
          </div>
          <span className="premium-gate-blocked__eyebrow">Offre Premium</span>
          <h2 id="premium-gate-title" className="premium-gate-blocked__title">
            Contenu réservé aux abonnés
          </h2>
          <p className="premium-gate-blocked__lede">
            <span className="premium-gate-blocked__featureName">{featureName}</span>
            {' '}
            fait partie des fonctionnalités Premium de StreamingBot.
          </p>
          <p className="premium-gate-blocked__meta">
            {paymentsEnabled ? (
              <>
                <strong className="premium-gate-blocked__price">4,99&nbsp;€</strong>
                <span className="premium-gate-blocked__per"> / mois</span>
                <span className="premium-gate-blocked__dot" aria-hidden>
                  {' · '}
                </span>
                <span>annulation à tout moment</span>
              </>
            ) : (
              <>Les paiements en ligne sont momentanément indisponibles ; l&apos;accès Premium peut être activé depuis votre espace ou par l&apos;équipe.</>
            )}
          </p>
          <div className="premium-gate-blocked__actions">
            <button type="button" className="premium-gate-button premium-gate-button--primary" onClick={goToPremiumOffer}>
              Voir l&apos;offre Premium
            </button>
            {eligibleForTrial && paymentsEnabled && (
              <button type="button" className="premium-gate-button premium-gate-button--trial" onClick={openTrialModal}>
                <FaGift className="premium-gate-button__icon" aria-hidden />
                Essai gratuit 30&nbsp;jours
              </button>
            )}
          </div>
        </div>
      </div>
      {showTrialRequest && (
        <TrialRequestModal
          userEmail={user?.email || null}
          onClose={handleTrialRequestClose}
          onConfirm={handleRequestTrial}
        />
      )}
    </>
  );
}

export default PremiumGate;
